import type { Cell, FormulaResult, FormulaValueData } from "@excel/cell";
// Chart runtime is accessed via the chart-host-registry slot rather
// than imported directly. See `chart-host-registry.ts` and
// `chart/install.ts` for the registration protocol. Types are pulled
// in via `import type` so bundlers emit zero runtime chart code unless
// the consumer imports `@cj-tech-master/excelts/chart`.
import { getChartSupport, tryGetChartSupport } from "@excel/chart-host-registry";
import type { ChartSupport } from "@excel/chart-host-registry";
import type { Chart, ChartAnchorModel } from "@excel/chart/chart";
import type {
  AddChartExFromRowsOptions,
  AddChartExFromTableOptions,
  AddChartFromRowsOptions,
  AddChartFromTableOptions,
  SeriesFromColumnsOptions
} from "@excel/chart/chart-api";
import type { AddChartExOptions, ChartExModel } from "@excel/chart/chart-ex-types";
import type { ExcelChartExPreset, ExcelChartPreset } from "@excel/chart/chart-presets";
import type {
  AddBarChartOptions,
  AddChartOptions,
  AddChartRange,
  AddComboChartOptions,
  AddPieChartOptions,
  AddScatterChartOptions,
  AddSurfaceChartOptions
} from "@excel/chart/types";
import { Column, type ColumnModel, type ColumnDefn } from "@excel/column";
import { DataValidations } from "@excel/data-validations";
import { Enums } from "@excel/enums";
import { ImageError, MergeConflictError, TableError } from "@excel/errors";
import {
  FormCheckbox,
  type FormCheckboxModel,
  type FormCheckboxOptions,
  type FormControlRange
} from "@excel/form-control";
import { Image, type ImageModel } from "@excel/image";
import { withPivotChartSource } from "@excel/pivot-chart";
import { makePivotTable, type PivotTable, type PivotTableModel } from "@excel/pivot-table";
import { Range, type RangeInput } from "@excel/range";
import { Row, type RowModel } from "@excel/row";
import type { AddSparklineGroupOptions, SparklineGroup } from "@excel/sparkline";
import { buildSparklineGroup } from "@excel/sparkline";
import { Table, type TableModel } from "@excel/table";
import type {
  AddImageRange,
  AddShapeOptions,
  AutoFilter,
  CellValue,
  ColBreak,
  ConditionalFormattingOptions,
  DataValidation,
  IgnoredError,
  RowBreak,
  RowValues,
  ShapeModel,
  Style,
  TableProperties,
  ThreadedComment,
  WatermarkOptions,
  WorksheetProperties,
  WorksheetState,
  WorksheetView
} from "@excel/types";
import { decodeCell, decodeRange, encodeCol, type Origin } from "@excel/utils/address";
import { getCellDisplayText } from "@excel/utils/cell-format";
import { colCache, type DecodedRange } from "@excel/utils/col-cache";
import { copyStyle } from "@excel/utils/copy-style";
import { isExternalImage } from "@excel/utils/drawing-utils";
import { applyMergeBorders, collectMergeBorders } from "@excel/utils/merge-borders";
import { buildSheetProtection } from "@excel/utils/sheet-protection";
import {
  calculateAutoFitWidth,
  getMaxDigitWidth,
  getColumnContentWidthPx,
  getCellTextWidthPx,
  getCellHeightPt
} from "@excel/utils/text-metrics";
import type { Workbook } from "@excel/workbook";

// Type for data validation model - maps address to validation
type DataValidationModel = { [address: string]: DataValidation | undefined };

interface SheetProtection {
  sheet?: boolean;
  objects?: boolean;
  scenarios?: boolean;
  selectLockedCells?: boolean;
  selectUnlockedCells?: boolean;
  formatCells?: boolean;
  formatColumns?: boolean;
  formatRows?: boolean;
  insertColumns?: boolean;
  insertRows?: boolean;
  insertHyperlinks?: boolean;
  deleteColumns?: boolean;
  deleteRows?: boolean;
  sort?: boolean;
  autoFilter?: boolean;
  pivotTables?: boolean;
  algorithmName?: string;
  hashValue?: string;
  saltValue?: string;
  spinCount?: number;
}

interface WorksheetOptions {
  workbook?: Workbook;
  id?: number;
  orderNo?: number;
  name?: string;
  state?: WorksheetState;
  properties?: Partial<WorksheetProperties>;
  pageSetup?: Partial<PageSetup>;
  headerFooter?: Partial<HeaderFooter>;
  views?: Partial<WorksheetView>[];
  autoFilter?: AutoFilter | null;
}

interface PageSetupMargins {
  left: number;
  right: number;
  top: number;
  bottom: number;
  header: number;
  footer: number;
}

interface PageSetup {
  margins: PageSetupMargins;
  orientation: string;
  horizontalDpi: number;
  verticalDpi: number;
  fitToPage: boolean;
  pageOrder: string;
  blackAndWhite: boolean;
  draft: boolean;
  cellComments: string;
  errors: string;
  scale: number;
  fitToWidth: number;
  fitToHeight: number;
  paperSize?: number;
  showRowColHeaders: boolean;
  showGridLines: boolean;
  firstPageNumber?: number;
  horizontalCentered: boolean;
  verticalCentered: boolean;
  rowBreaks: RowBreak[];
  printArea?: string;
  printTitlesRow?: string;
  printTitlesColumn?: string;
}

interface HeaderFooter {
  differentFirst: boolean;
  differentOddEven: boolean;
  oddHeader: string | null;
  oddFooter: string | null;
  evenHeader: string | null;
  evenFooter: string | null;
  firstHeader: string | null;
  firstFooter: string | null;
}

interface WorksheetModel {
  id: number;
  name: string;
  dataValidations: DataValidationModel;
  properties: Partial<WorksheetProperties>;
  state: WorksheetState;
  pageSetup: PageSetup;
  headerFooter: HeaderFooter;
  rowBreaks: RowBreak[];
  colBreaks: ColBreak[];
  views: Partial<WorksheetView>[];
  autoFilter: AutoFilter | null;
  media: ImageModel[];
  shapes?: ShapeModel[];
  sheetProtection: SheetProtection | null;
  tables: TableModel[];
  pivotTables: PivotTable[];
  conditionalFormattings: ConditionalFormattingOptions[];
  formControls: FormCheckboxModel[];
  ignoredErrors: IgnoredError[];
  watermark?: WatermarkOptions | null;
  cols?: ColumnModel[];
  rows?: RowModel[];
  dimensions?: Range;
  mergeCells?: string[];
  /** Loaded drawing data (for charts, etc.) - preserved for round-trip */
  drawing?: unknown;
  /** Chart anchor models for worksheet charts */
  charts?: ChartAnchorModel[];
  /** Sparkline groups (x14:sparklineGroups) */
  sparklineGroups?: SparklineGroup[];
  /**
   * Office 365 threaded comments for this worksheet. Rendered as a
   * separate `xl/threadedComments/threadedComment{N}.xml` part. Empty
   * when the sheet has no modern comments.
   */
  threadedComments?: Array<{ ref: string; comment: ThreadedComment }>;
}

// Worksheet requirements
//  Operate as sheet inside workbook or standalone
//  Load and Save from file and stream
//  Access/Add/Delete individual cells
//  Manage column widths and row heights

class Worksheet {
  // Type declarations only - no runtime overhead
  declare private _workbook: Workbook;
  declare public id: number;
  declare public orderNo: number;
  declare private _name: string;
  declare public state: WorksheetState;
  declare private _rows: Row[];
  declare private _columns: Column[];
  declare private _keys: { [key: string]: Column };
  declare private _merges: { [key: string]: Range };
  declare public rowBreaks: RowBreak[];
  declare public colBreaks: ColBreak[];
  declare public properties: Partial<WorksheetProperties>;
  declare public pageSetup: PageSetup;
  declare public headerFooter: HeaderFooter;
  declare public dataValidations: DataValidations;
  declare public views: Partial<WorksheetView>[];
  declare public autoFilter: AutoFilter | null;
  declare private _media: Image[];
  declare private _shapes: ShapeModel[];
  declare private _charts: Chart[];
  declare private _sparklineGroups: SparklineGroup[];
  declare public sheetProtection: SheetProtection | null;
  declare public tables: { [key: string]: Table };
  declare public pivotTables: PivotTable[];
  declare public conditionalFormattings: ConditionalFormattingOptions[];
  declare public formControls: FormCheckbox[];
  declare public ignoredErrors: IgnoredError[];
  /**
   * Office 365 threaded comments attached to this sheet, keyed by
   * cell reference. Each cell may carry a linear conversation (a
   * top-level comment followed by replies whose `parentId` points at
   * the top-level entry). Round-tripped verbatim on load; mutating
   * the array triggers a fresh `xl/threadedComments/threadedComment{N}.xml`
   * on save.
   *
   * Distinct from (and can coexist with) classic VML notes — modern
   * Excel shows threaded comments in the conversation pane, classic
   * VML in the tooltip. When both are present the threaded text is
   * authoritative.
   */
  declare public threadedComments: Array<{ ref: string; comment: ThreadedComment }>;
  declare private _headerRowCount?: number;
  /** Loaded drawing data (for charts, etc.) - preserved for round-trip */
  declare private _drawing: unknown;
  /** Watermark configuration for overlay or header mode */
  declare private _watermark: WatermarkOptions | null;

  constructor(options: WorksheetOptions) {
    this._workbook = options.workbook!;

    // in a workbook, each sheet will have a number
    this.id = options.id ?? 0;
    this.orderNo = options.orderNo ?? 0;

    // and a name - use the setter to ensure validation and truncation
    this.name = options.name || `sheet${this.id}`;

    // add a state
    this.state = options.state ?? "visible";

    // rows allows access organised by row. Sparse array of arrays indexed by row-1, col
    // Note: _rows is zero based. Must subtract 1 to go from cell.row to index
    this._rows = [];

    // column definitions
    this._columns = [];

    // column keys (addRow convenience): key ==> this._collumns index
    this._keys = {};

    // keep record of all merges
    this._merges = {};

    // record of all row and column pageBreaks
    this.rowBreaks = [];
    this.colBreaks = [];

    // for tabColor, default row height, outline levels, etc
    this.properties = {
      defaultRowHeight: 15,
      outlineLevelCol: 0,
      outlineLevelRow: 0,
      ...options.properties
    };

    // for all things printing
    this.pageSetup = {
      margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
      orientation: "portrait",
      // Excel does not normally write these unless explicitly set.
      // Historically we used 4294967295 as a sentinel when parsing, but emitting it
      // can cause strict Excel parsers to treat the workbook as corrupted.
      horizontalDpi: undefined,
      verticalDpi: undefined,
      fitToPage: !!(
        options.pageSetup &&
        (options.pageSetup.fitToWidth || options.pageSetup.fitToHeight) &&
        !options.pageSetup.scale
      ),
      pageOrder: "downThenOver",
      blackAndWhite: false,
      draft: false,
      cellComments: "None",
      errors: "displayed",
      scale: 100,
      fitToWidth: 1,
      fitToHeight: 1,
      paperSize: undefined,
      showRowColHeaders: false,
      showGridLines: false,
      firstPageNumber: undefined,
      horizontalCentered: false,
      verticalCentered: false,
      rowBreaks: null,
      colBreaks: null,
      ...options.pageSetup
    } as PageSetup;

    this.headerFooter = {
      differentFirst: false,
      differentOddEven: false,
      oddHeader: null,
      oddFooter: null,
      evenHeader: null,
      evenFooter: null,
      firstHeader: null,
      firstFooter: null,
      ...options.headerFooter
    };

    this.dataValidations = new DataValidations();

    // for freezepanes, split, zoom, gridlines, etc
    this.views = options.views ?? [];

    this.autoFilter = options.autoFilter ?? null;

    // for images, etc
    this._media = [];

    // for user-drawn shapes (rectangles, lines, text boxes, …)
    this._shapes = [];

    // for charts
    this._charts = [];
    this._sparklineGroups = [];

    // worksheet protection
    this.sheetProtection = null;

    // for tables
    this.tables = {};

    this.pivotTables = [];

    this.conditionalFormattings = [];

    // for form controls (legacy checkboxes, etc.)
    this.formControls = [];

    // ignored errors (suppress green triangles in Excel)
    this.ignoredErrors = [];

    // Office 365 threaded comments (separate from classic VML notes).
    this.threadedComments = [];

    // watermark configuration
    this._watermark = null;
  }

  get name(): string {
    return this._name;
  }

  set name(name: string | undefined) {
    if (name === undefined) {
      name = `sheet${this.id}`;
    }

    if (this._name === name) {
      return;
    }

    // Delegate to the workbook-level validator so both worksheets and
    // chartsheets share a single naming namespace. Previously this
    // setter only cross-checked against other worksheets, allowing a
    // chartsheet named "S" to coexist with a worksheet named "S";
    // Excel itself forbids that collision. `validateSheetName`
    // performs the full type / empty / illegal-char / quote / length
    // / case-insensitive duplicate checks and returns the sanitised
    // name (truncated to 31 chars if needed).
    this._name = this._workbook.validateSheetName(name, this);
  }

  /**
   * The workbook that contains this worksheet
   */
  get workbook(): Workbook {
    return this._workbook;
  }

  /**
   * When you're done with this worksheet, call this to remove from workbook
   */
  destroy(): void {
    this._workbook.removeWorksheetEx(this);
  }

  /**
   * Get the bounding range of the cells in this worksheet
   */
  get dimensions(): Range {
    const dimensions = new Range();
    this._rows.forEach(row => {
      if (row) {
        const rowDims = row.dimensions;
        if (rowDims) {
          dimensions.expand(row.number, rowDims.min, row.number, rowDims.max);
        }
      }
    });
    return dimensions;
  }

  // =========================================================================
  // Columns

  /**
   * Get the current columns array
   */
  get columns(): Column[] {
    return this._columns;
  }

  /**
   * Add column headers and define column keys and widths.
   *
   * Note: these column structures are a workbook-building convenience only,
   * apart from the column width, they will not be fully persisted.
   */
  set columns(value: ColumnDefn[]) {
    // calculate max header row count
    this._headerRowCount = value.reduce((pv, cv) => {
      const headerCount = Array.isArray(cv.header) ? cv.header.length : cv.header ? 1 : 0;
      return Math.max(pv, headerCount);
    }, 0);

    // construct Column objects
    let count = 1;
    const columns: Column[] = (this._columns = []);
    value.forEach(defn => {
      const column = new Column(this, count++, false);
      columns.push(column);
      column.defn = defn;
    });
  }

  getColumnKey(key: string): Column | undefined {
    return this._keys[key];
  }

  setColumnKey(key: string, value: Column): void {
    this._keys[key] = value;
  }

  deleteColumnKey(key: string): void {
    delete this._keys[key];
  }

  eachColumnKey(f: (column: Column, key: string) => void): void {
    Object.keys(this._keys).forEach(key => f(this._keys[key], key));
  }

  /**
   * Access an individual column by key, letter and 1-based column number
   */
  getColumn(c: string | number): Column {
    let colNum: number;
    if (typeof c === "string") {
      // if it matches a key'd column, return that
      const col = this._keys[c];
      if (col) {
        return col;
      }

      // otherwise, assume letter
      colNum = colCache.l2n(c);
    } else {
      colNum = c;
    }
    if (colNum > this._columns.length) {
      let n = this._columns.length + 1;
      while (n <= colNum) {
        this._columns.push(new Column(this, n++));
      }
    }
    return this._columns[colNum - 1];
  }

  /**
   * Cut one or more columns (columns to the right are shifted left)
   * and optionally insert more
   *
   * If column properties have been defined, they will be cut or moved accordingly
   *
   * Known limitation: If a splice causes any merged cells to move, the results may be unpredictable
   *
   * Also: If the worksheet has more rows than values in the column inserts,
   * the rows will still be shifted as if the values existed
   */
  spliceColumns(start: number, count: number, ...inserts: CellValue[][]): void {
    // Before splicing cells, release all cell-level merge references so that
    // row.splice copies plain values instead of merge proxies.
    // _spliceMerges (called later) will rebuild cell-level refs at new coordinates.
    for (const merge of Object.values(this._merges)) {
      for (let r = merge.top; r <= merge.bottom; r++) {
        for (let c = merge.left; c <= merge.right; c++) {
          const cell = this.findCell(r, c);
          if (cell && cell.type === Enums.ValueType.Merge) {
            cell.unmerge();
          }
        }
      }
    }

    const rows = this._rows;
    const nRows = rows.length;
    if (inserts.length > 0) {
      // must iterate over all rows whether they exist yet or not
      for (let i = 0; i < nRows; i++) {
        const insertValues = inserts.map(insert => insert[i] ?? null);
        const row = this.getRow(i + 1);
        row.splice(start, count, ...insertValues);
      }
    } else {
      // nothing to insert, so just splice all rows
      this._rows.forEach(r => {
        if (r) {
          r.splice(start, count);
        }
      });
    }

    // splice column definitions
    const nExpand = inserts.length - count;
    const nKeep = start + count;
    const nEnd = this._columns.length;
    if (nExpand < 0) {
      for (let i = start + inserts.length; i <= nEnd; i++) {
        this.getColumn(i).defn = this.getColumn(i - nExpand).defn;
      }
    } else if (nExpand > 0) {
      for (let i = nEnd; i >= nKeep; i--) {
        this.getColumn(i + nExpand).defn = this.getColumn(i).defn;
      }
    }
    for (let i = start; i < start + inserts.length; i++) {
      this.getColumn(i).defn = undefined;
    }

    // account for defined names
    this.workbook.definedNames.spliceColumns(this.name, start, count, inserts.length);

    // account for images
    if (nExpand !== 0) {
      for (const image of this._media) {
        if (image.type === "image" && image.range) {
          const { tl, br } = image.range;
          if (tl && tl.nativeCol >= start - 1) {
            tl.nativeCol = Math.max(0, tl.nativeCol + nExpand);
          }
          if (br && br.nativeCol >= start - 1) {
            br.nativeCol = Math.max(0, br.nativeCol + nExpand);
          }
        }
      }

      // account for chart anchors in drawing and programmatic chart objects
      this._shiftChartAnchors("col", start - 1, nExpand);
    }

    // account for merges
    this._spliceMerges("col", start, count, inserts.length);
  }

  /**
   * Get the last column in a worksheet
   */
  get lastColumn(): Column {
    return this.getColumn(this.columnCount);
  }

  /**
   * The total column size of the document. Equal to the maximum cell count from all of the rows
   */
  get columnCount(): number {
    let maxCount = 0;
    this.eachRow(row => {
      maxCount = Math.max(maxCount, row.cellCount);
    });
    return maxCount;
  }

  /**
   * A count of the number of columns that have values
   */
  get actualColumnCount(): number {
    // performance nightmare - for each row, counts all the columns used
    const counts: boolean[] = [];
    let count = 0;
    this.eachRow(row => {
      row.eachCell(({ col }: { col: number }) => {
        if (!counts[col]) {
          counts[col] = true;
          count++;
        }
      });
    });
    return count;
  }

  // =========================================================================
  // Rows

  /** @internal */
  _commitRow(row: Row): void {
    // nop - allows streaming reader to fill a document
  }

  private get _lastRowNumber(): number {
    // need to cope with results of splice
    const rows = this._rows;
    let n = rows.length;
    while (n > 0 && rows[n - 1] === undefined) {
      n--;
    }
    return n;
  }

  private get _nextRow(): number {
    return this._lastRowNumber + 1;
  }

  /**
   * Get the last editable row in a worksheet (or undefined if there are none)
   */
  get lastRow(): Row | undefined {
    if (this._rows.length) {
      return this._rows[this._rows.length - 1];
    }
    return undefined;
  }

  /**
   * Tries to find and return row for row number, else undefined
   *
   * @param r - The 1-indexed row number
   */
  findRow(r: number): Row | undefined {
    return this._rows[r - 1];
  }

  /**
   * Tries to find and return rows for row number start and length, else undefined
   *
   * @param start - The 1-indexed starting row number
   * @param length - The length of the expected array
   */
  findRows(start: number, length: number): (Row | undefined)[] {
    return this._rows.slice(start - 1, start - 1 + length);
  }

  /**
   * The total row size of the document. Equal to the row number of the last row that has values.
   */
  get rowCount(): number {
    return this._lastRowNumber;
  }

  /**
   * A count of the number of rows that have values. If a mid-document row is empty, it will not be included in the count.
   */
  get actualRowCount(): number {
    // counts actual rows that have actual data
    let count = 0;
    this.eachRow(() => {
      count++;
    });
    return count;
  }

  // get a row by row number.
  getRow(r: number): Row {
    let row = this._rows[r - 1];
    if (!row) {
      row = this._rows[r - 1] = new Row(this, r);
    }
    return row;
  }

  // get multiple rows by row number.
  getRows(start: number, length: number): Row[] | undefined {
    if (length < 1) {
      return undefined;
    }
    const rows: Row[] = [];
    for (let i = start; i < start + length; i++) {
      rows.push(this.getRow(i));
    }
    return rows;
  }

  addRow(value: RowValues, style: string = "n"): Row {
    const rowNo = this._nextRow;
    const row = this.getRow(rowNo);
    row.values = value;
    this._setStyleOption(rowNo, style[0] === "i" ? style : "n");
    return row;
  }

  addRows(value: RowValues[], style: string = "n"): Row[] {
    const rows: Row[] = [];
    value.forEach(row => {
      rows.push(this.addRow(row, style));
    });
    return rows;
  }

  insertRow(pos: number, value: RowValues, style: string = "n"): Row {
    this.spliceRows(pos, 0, value);
    this._setStyleOption(pos, style);
    return this.getRow(pos);
  }

  insertRows(pos: number, values: RowValues[], style: string = "n"): Row[] | undefined {
    this.spliceRows(pos, 0, ...values);
    if (style !== "n") {
      // copy over the styles
      for (let i = 0; i < values.length; i++) {
        if (style[0] === "o" && this.findRow(values.length + pos + i) !== undefined) {
          this._copyStyle(values.length + pos + i, pos + i, style[1] === "+");
        } else if (style[0] === "i" && this.findRow(pos - 1) !== undefined) {
          this._copyStyle(pos - 1, pos + i, style[1] === "+");
        }
      }
    }
    return this.getRows(pos, values.length);
  }

  // set row at position to same style as of either pervious row (option 'i') or next row (option 'o')
  private _setStyleOption(pos: number, style: string = "n"): void {
    if (style[0] === "o" && this.findRow(pos + 1) !== undefined) {
      this._copyStyle(pos + 1, pos, style[1] === "+");
    } else if (style[0] === "i" && this.findRow(pos - 1) !== undefined) {
      this._copyStyle(pos - 1, pos, style[1] === "+");
    }
  }

  private _copyStyle(src: number, dest: number, styleEmpty: boolean = false): void {
    const rSrc = this.getRow(src);
    const rDst = this.getRow(dest);
    rDst.style = (copyStyle(rSrc.style) as Partial<Style>) ?? {};
    rSrc.eachCell({ includeEmpty: styleEmpty }, (cell: Cell, colNumber: number) => {
      rDst.getCell(colNumber).style = (copyStyle(cell.style) as Partial<Style>) ?? {};
    });
    rDst.height = rSrc.height;
  }

  /**
   * Duplicate rows and insert new rows
   */
  duplicateRow(rowNum: number, count: number, insert: boolean = false): void {
    // create count duplicates of rowNum
    // either inserting new or overwriting existing rows

    const rSrc = this.getRow(rowNum);
    const inserts = Array.from<RowValues>({ length: count }).fill(rSrc.values);

    // Collect single-row merges from the source row before splicing
    // (only merges where top == bottom == rowNum, i.e. horizontal merges within one row)
    const srcMerges: Range[] = [];
    for (const merge of Object.values(this._merges)) {
      if (merge.top === rowNum && merge.bottom === rowNum) {
        srcMerges.push(merge);
      }
    }

    // Collect images anchored to the source row before splicing
    // (images whose top-left anchor is on the source row)
    const srcImages: Image[] = [];
    const srcRow0 = rowNum - 1; // 0-based source row
    for (const image of this._media) {
      if (image.type === "image" && image.range) {
        if (image.range.tl.nativeRow === srcRow0) {
          srcImages.push(image);
        }
      }
    }

    this.spliceRows(rowNum + 1, insert ? 0 : count, ...inserts);

    // now copy styles...
    for (let i = 0; i < count; i++) {
      const rDst = this._rows[rowNum + i];
      rDst.style = copyStyle(rSrc.style) ?? {};
      rDst.height = rSrc.height;
      rSrc.eachCell({ includeEmpty: true }, (cell: Cell, colNumber: number) => {
        rDst.getCell(colNumber).style = copyStyle(cell.style) ?? {};
      });
    }

    // Duplicate single-row merges from source row into each new row
    if (srcMerges.length > 0) {
      for (let i = 0; i < count; i++) {
        const dstRow = rowNum + 1 + i;

        // In overwrite mode, clear any existing merges in the target row
        if (!insert) {
          const toRemove: string[] = [];
          for (const [key, merge] of Object.entries(this._merges)) {
            if (merge.top <= dstRow && merge.bottom >= dstRow) {
              toRemove.push(key);
            }
          }
          for (const key of toRemove) {
            this._unMergeMaster(this.getCell(key));
          }
        }

        for (const srcMerge of srcMerges) {
          this.mergeCellsWithoutStyle(dstRow, srcMerge.left, dstRow, srcMerge.right);
        }
      }
    }

    // Duplicate images from source row into each new row.
    // In overwrite mode, first remove any images anchored to the target rows
    // so they don't coexist with the clones (mirrors merge cleanup above).
    if (!insert) {
      const dstStart0 = rowNum; // first target row, 0-based (1-based rowNum + 1 → 0-based rowNum)
      const dstEnd0 = rowNum + count - 1; // last target row, 0-based
      this._media = this._media.filter(image => {
        if (image.type === "image" && image.range) {
          const row0 = image.range.tl.nativeRow;
          return row0 < dstStart0 || row0 > dstEnd0;
        }
        return true;
      });
    }

    for (let i = 0; i < count; i++) {
      const rowDelta = i + 1; // offset from source row to target row
      for (const srcImage of srcImages) {
        const cloned = srcImage.clone();
        cloned.range!.tl.nativeRow = srcRow0 + rowDelta;
        if (cloned.range!.br) {
          const brDelta = srcImage.range!.br!.nativeRow - srcRow0;
          cloned.range!.br.nativeRow = srcRow0 + rowDelta + brDelta;
        }
        this._media.push(cloned);
      }
    }
  }

  /**
   * Cut one or more rows (rows below are shifted up)
   * and optionally insert more
   *
   * Known limitation: If a splice causes any merged cells to move, the results may be unpredictable
   */
  spliceRows(start: number, count: number, ...inserts: RowValues[]): void {
    // same problem as row.splice, except worse.

    // Before splicing rows, release all cell-level merge references so that
    // row value copies work on plain values instead of merge proxies.
    // _spliceMerges (called later) will rebuild cell-level refs at new coordinates.
    for (const merge of Object.values(this._merges)) {
      for (let r = merge.top; r <= merge.bottom; r++) {
        for (let c = merge.left; c <= merge.right; c++) {
          const cell = this.findCell(r, c);
          if (cell && cell.type === Enums.ValueType.Merge) {
            cell.unmerge();
          }
        }
      }
    }

    const nKeep = start + count;
    const nInserts = inserts.length;
    const nExpand = nInserts - count;
    const nEnd = this._rows.length;
    let i: number;
    let rSrc: Row | undefined;
    if (nExpand < 0) {
      // remove rows
      if (start === nEnd) {
        this._rows[nEnd - 1] = undefined!;
      }
      for (i = nKeep; i <= nEnd; i++) {
        rSrc = this._rows[i - 1];
        if (rSrc) {
          const rDst = this.getRow(i + nExpand);
          rDst.values = rSrc.values;
          rDst.style = (copyStyle(rSrc.style) as Partial<Style>) ?? {};
          rDst.height = rSrc.height;
          rSrc.eachCell({ includeEmpty: true }, (cell: Cell, colNumber: number) => {
            rDst.getCell(colNumber).style = (copyStyle(cell.style) as Partial<Style>) ?? {};
          });
          this._rows[i - 1] = undefined!;
        } else {
          this._rows[i + nExpand - 1] = undefined!;
        }
      }
    } else if (nExpand > 0) {
      // insert new cells
      for (i = nEnd; i >= nKeep; i--) {
        rSrc = this._rows[i - 1];
        if (rSrc) {
          const rDst = this.getRow(i + nExpand);
          rDst.values = rSrc.values;
          rDst.style = (copyStyle(rSrc.style) as Partial<Style>) ?? {};
          rDst.height = rSrc.height;
          rSrc.eachCell({ includeEmpty: true }, (cell: Cell, colNumber: number) => {
            rDst.getCell(colNumber).style = (copyStyle(cell.style) as Partial<Style>) ?? {};
          });
        } else {
          this._rows[i + nExpand - 1] = undefined!;
        }
      }
    }

    // now copy over the new values
    for (i = 0; i < nInserts; i++) {
      const rDst = this.getRow(start + i);
      rDst.style = {};
      rDst.values = inserts[i];
    }

    // account for defined names
    this.workbook.definedNames.spliceRows(this.name, start, count, nInserts);

    // account for images
    if (nExpand !== 0) {
      for (const image of this._media) {
        if (image.type === "image" && image.range) {
          const { tl, br } = image.range;
          if (tl && tl.nativeRow >= start - 1) {
            tl.nativeRow = Math.max(0, tl.nativeRow + nExpand);
          }
          if (br && br.nativeRow >= start - 1) {
            br.nativeRow = Math.max(0, br.nativeRow + nExpand);
          }
        }
      }

      // account for chart anchors in drawing and programmatic chart objects
      this._shiftChartAnchors("row", start - 1, nExpand);
    }

    // account for merges
    this._spliceMerges("row", start, count, nInserts);
  }

  /**
   * Iterate over all rows that have values in a worksheet
   */
  eachRow(callback: (row: Row, rowNumber: number) => void): void;
  /**
   * Iterate over all rows (including empty rows) in a worksheet
   */
  eachRow(opt: { includeEmpty?: boolean }, callback: (row: Row, rowNumber: number) => void): void;
  eachRow(
    optOrCallback: { includeEmpty?: boolean } | ((row: Row, rowNumber: number) => void),
    maybeCallback?: (row: Row, rowNumber: number) => void
  ): void {
    let options: { includeEmpty?: boolean } | undefined;
    let callback: (row: Row, rowNumber: number) => void;
    if (typeof optOrCallback === "function") {
      callback = optOrCallback;
    } else {
      options = optOrCallback;
      callback = maybeCallback!;
    }
    if (options && options.includeEmpty) {
      const n = this._rows.length;
      for (let i = 1; i <= n; i++) {
        callback(this.getRow(i), i);
      }
    } else {
      this._rows.forEach(row => {
        if (row && row.hasValues) {
          callback(row, row.number);
        }
      });
    }
  }

  /**
   * Return all rows as sparse array
   */
  getSheetValues(): CellValue[][] {
    const rows: CellValue[][] = [];
    this._rows.forEach(row => {
      if (row) {
        rows[row.number] = row.values;
      }
    });
    return rows;
  }

  // =========================================================================
  // Cells

  /**
   * Returns the cell at [r,c] or address given by r. If not found, return undefined
   */
  findCell(r: number | string, c?: number): Cell | undefined {
    const address = colCache.getAddress(r, c);
    const row = this._rows[address.row - 1];
    return row ? row.findCell(address.col) : undefined;
  }

  /**
   * Get or create cell at [r,c] or address given by r
   */
  getCell(r: number | string, c?: number): Cell {
    const address = colCache.getAddress(r, c);
    const row = this.getRow(address.row);
    return row.getCellEx(address);
  }

  // =========================================================================
  // Merge

  /**
   * Merge cells, either:
   *
   * tlbr string, e.g. `'A4:B5'`
   *
   * tl string, br string, e.g. `'G10', 'H11'`
   *
   * t, l, b, r numbers, e.g. `10,11,12,13`
   */
  mergeCells(...cells: RangeInput[]): void {
    const dimensions = new Range(cells);
    this._mergeCellsInternal(dimensions);
  }

  mergeCellsWithoutStyle(...cells: RangeInput[]): void {
    const dimensions = new Range(cells);
    this._mergeCellsInternal(dimensions, true);
  }

  private _mergeCellsInternal(dimensions: Range, ignoreStyle?: boolean): void {
    // check cells aren't already merged
    Object.values(this._merges).forEach((merge: Range) => {
      if (merge.intersects(dimensions)) {
        throw new MergeConflictError();
      }
    });

    const { top, left, bottom, right } = dimensions;

    // Collect perimeter borders BEFORE merge overwrites slave styles
    const collected = ignoreStyle
      ? undefined
      : collectMergeBorders(top, left, bottom, right, (r, c) => this.findCell(r, c) as any);

    // Apply merge — slave cells inherit the master's full style
    const master = this.getCell(dimensions.top, dimensions.left);
    for (let i = top; i <= bottom; i++) {
      for (let j = left; j <= right; j++) {
        if (i > top || j > left) {
          this.getCell(i, j).merge(master, ignoreStyle);
        }
      }
    }

    // Reconstruct position-aware borders (like Excel):
    // outer borders survive, inner borders are cleared.
    if (collected) {
      applyMergeBorders(top, left, bottom, right, collected, (r, c) => this.getCell(r, c) as any);
    }

    // index merge
    this._merges[master.address] = dimensions;
  }

  private _unMergeMaster(master: Cell): void {
    // master is always top left of a rectangle
    const merge = this._merges[master.address];
    if (merge) {
      for (let i = merge.top; i <= merge.bottom; i++) {
        for (let j = merge.left; j <= merge.right; j++) {
          this.getCell(i, j).unmerge();
        }
      }
      delete this._merges[master.address];
    }
  }

  /**
   * Shift all chart anchors (drawing anchors + programmatic charts) by delta along an axis.
   * @param axis "row" or "col"
   * @param threshold 0-based index; anchors at or beyond this are shifted
   * @param delta number of positions to shift (positive = expand, negative = shrink)
   */
  private _shiftChartAnchors(axis: "row" | "col", threshold: number, delta: number): void {
    const prop = axis === "row" ? "nativeRow" : "nativeCol";

    // Drawing anchors (from loaded file)
    const drawing = this._drawing as any;
    if (drawing?.anchors) {
      for (const anchor of drawing.anchors) {
        const tl = anchor.range?.tl;
        const br = anchor.range?.br;
        if (tl && tl[prop] >= threshold) {
          tl[prop] = Math.max(0, tl[prop] + delta);
        }
        if (br && br[prop] >= threshold) {
          br[prop] = Math.max(0, br[prop] + delta);
        }
      }
    }

    // Programmatic chart objects
    for (const chart of this._charts) {
      const { tl, br } = chart.range;
      if (tl[prop] >= threshold) {
        tl[prop] = Math.max(0, tl[prop] + delta);
      }
      if (br && br[prop] >= threshold) {
        br[prop] = Math.max(0, br[prop] + delta);
      }
    }
  }

  /**
   * Update _merges dictionary and cell-level merge references after a row or column splice.
   */
  private _spliceMerges(axis: "row" | "col", start: number, count: number, nInserts: number): void {
    const nExpand = nInserts - count;
    if (nExpand === 0 && count === 0) {
      return;
    }
    const nKeep = start + count;
    const isRow = axis === "row";

    const newMerges: Record<string, Range> = {};

    for (const merge of Object.values(this._merges)) {
      const { top, left, bottom, right } = merge.model;
      // For row axis: lo=top, hi=bottom. For col axis: lo=left, hi=right.
      const lo = isRow ? top : left;
      const hi = isRow ? bottom : right;

      if (nExpand <= 0 && count > 0) {
        // Deleting rows/columns
        const deleteEnd = nKeep - 1;
        if (lo > deleteEnd) {
          // Entirely after deleted range — shift
          const newRange = isRow
            ? new Range(top + nExpand, left, bottom + nExpand, right)
            : new Range(top, left + nExpand, bottom, right + nExpand);
          newMerges[colCache.encodeAddress(newRange.top, newRange.left)] = newRange;
        } else if (hi < start) {
          // Entirely before deleted range — unchanged
          newMerges[colCache.encodeAddress(top, left)] = merge;
        } else if (lo >= start && hi <= deleteEnd) {
          // Entirely within deleted range — remove
        } else {
          // Spans splice boundary — shrink
          let newTop = top;
          let newLeft = left;
          let newBottom = bottom;
          let newRight = right;
          if (isRow) {
            newTop = top < start ? top : start;
            newBottom = Math.max(newTop, bottom + nExpand);
          } else {
            newLeft = left < start ? left : start;
            newRight = Math.max(newLeft, right + nExpand);
          }
          const newRange = new Range(newTop, newLeft, newBottom, newRight);
          if (newTop === newBottom && newLeft === newRight) {
            // Degenerate 1x1 merge — remove instead of keeping
          } else {
            newMerges[colCache.encodeAddress(newRange.top, newRange.left)] = newRange;
          }
        }
      } else {
        // Inserting rows/columns: shift items at/after nKeep
        if (lo >= nKeep) {
          // Entirely at or after splice — shift
          const newRange = isRow
            ? new Range(top + nExpand, left, bottom + nExpand, right)
            : new Range(top, left + nExpand, bottom, right + nExpand);
          newMerges[colCache.encodeAddress(newRange.top, newRange.left)] = newRange;
        } else if (hi < nKeep) {
          // Entirely before splice — unchanged
          newMerges[colCache.encodeAddress(top, left)] = merge;
        } else {
          // Spans splice boundary — stretch
          if (isRow) {
            merge.model.bottom = bottom + nExpand;
          } else {
            merge.model.right = right + nExpand;
          }
          newMerges[colCache.encodeAddress(top, left)] = merge;
        }
      }
    }

    this._merges = newMerges;

    // Rebuild cell-level merge references for all merges.
    // Pre-unmerge in spliceRows/spliceColumns clears all cell refs,
    // so we must rebuild every merge, not just moved/resized ones.
    for (const m of Object.values(newMerges)) {
      const master = this.getCell(m.top, m.left);
      for (let r = m.top; r <= m.bottom; r++) {
        for (let c = m.left; c <= m.right; c++) {
          if (r > m.top || c > m.left) {
            this.getCell(r, c).merge(master, true);
          }
        }
      }
    }
  }

  get hasMerges(): boolean {
    // return true if this._merges has a merge object
    return Object.values(this._merges).some(Boolean);
  }

  /**
   * Read-only enumeration of every merged region on this sheet
   * (1-based, inclusive). Consumed by the formula engine's snapshot
   * builder to detect `#SPILL!` conflicts. See issue #162 follow-up.
   */
  get mergedRegions(): ReadonlyArray<{
    readonly top: number;
    readonly left: number;
    readonly bottom: number;
    readonly right: number;
  }> {
    return Object.values(this._merges).map(merge => ({
      top: merge.top,
      left: merge.left,
      bottom: merge.bottom,
      right: merge.right
    }));
  }

  /**
   * Scan the range and if any cell is part of a merge, un-merge the group.
   * Note this function can affect multiple merges and merge-blocks are
   * atomic - either they're all merged or all un-merged.
   */
  unMergeCells(...cells: RangeInput[]): void {
    const dimensions = new Range(cells);

    // find any cells in that range and unmerge them
    for (let i = dimensions.top; i <= dimensions.bottom; i++) {
      for (let j = dimensions.left; j <= dimensions.right; j++) {
        const cell = this.findCell(i, j);
        if (cell) {
          if (cell.type === Enums.ValueType.Merge) {
            // this cell merges to another master
            this._unMergeMaster(cell.master);
          } else if (this._merges[cell.address]) {
            // this cell is a master
            this._unMergeMaster(cell);
          }
        }
      }
    }
  }

  // ===========================================================================
  // Shared/Array Formula
  fillFormula(
    range: string,
    formula: string,
    results?:
      | FormulaResult[][]
      | FormulaResult[]
      | ((row: number, col: number) => FormulaResult | undefined),
    shareType: string = "shared"
  ): void {
    // Define formula for top-left cell and share to rest
    const decoded = colCache.decode(range) as DecodedRange;
    const { top, left, bottom, right } = decoded;
    const width = right - left + 1;
    const masterAddress = colCache.encodeAddress(top, left);
    const isShared = shareType === "shared";

    // work out result accessor
    let getResult: (row: number, col: number) => FormulaResult | undefined;
    if (typeof results === "function") {
      getResult = results;
    } else if (Array.isArray(results)) {
      if (Array.isArray(results[0])) {
        getResult = (row: number, col: number) =>
          (results as FormulaResult[][])[row - top][col - left];
      } else {
        getResult = (row: number, col: number) =>
          (results as FormulaResult[])[(row - top) * width + (col - left)];
      }
    } else {
      getResult = () => undefined;
    }
    let first = true;
    for (let r = top; r <= bottom; r++) {
      for (let c = left; c <= right; c++) {
        if (first) {
          const cell = this.getCell(r, c);
          const formulaValue: FormulaValueData = {
            shareType,
            formula,
            ref: range,
            result: getResult(r, c)
          };
          cell.value = formulaValue as CellValue;
          first = false;
        } else {
          this.getCell(r, c).value = isShared
            ? {
                sharedFormula: masterAddress,
                result: getResult(r, c)
              }
            : getResult(r, c);
        }
      }
    }
  }

  // =========================================================================
  // Images

  /**
   * Using the image id from `Workbook.addImage`,
   * embed an image within the worksheet to cover a range
   */
  addImage(imageId: string | number, range: AddImageRange): void {
    const model = {
      type: "image",
      imageId: String(imageId),
      range
    };
    this._media.push(new Image(this, model));
  }

  /**
   * Add a free-form drawing shape (rectangle, ellipse, line, text box, …) to
   * the worksheet, anchored to a cell range.
   *
   * Unlike images, shapes need no media file — the geometry, fill, outline and
   * optional text label are written directly into the drawing part.
   *
   * @example
   * ```typescript
   * worksheet.addShape({
   *   type: "rect",
   *   range: "B2:D5",
   *   fillColor: "FFD966",
   *   lineColor: "000000",
   *   lineWidth: 1,
   *   text: "Important"
   * });
   * ```
   */
  addShape(options: AddShapeOptions): void {
    const range = options.range;
    // A shape must cover an area, mirroring images. Reject inputs that resolve
    // to no size up front, with a clear shape-specific message — otherwise the
    // failure surfaces much later as a confusing `ImageError` from the internal
    // range parser when the worksheet is serialized.
    const hasArea =
      (typeof range === "string" && range.includes(":")) ||
      (typeof range === "object" &&
        range !== null &&
        ("br" in range || "ext" in range || "pos" in range));
    if (!hasArea) {
      throw new ImageError(
        'addShape requires a range covering an area: a cell range like "B2:D5", or an object with `br`, `ext`, or `pos`.'
      );
    }
    this._shapes.push({
      type: "shape",
      shapeType: options.type ?? "rect",
      range,
      fillColor: options.fillColor,
      lineColor: options.lineColor,
      lineWidth: options.lineWidth,
      text: options.text,
      name: options.name
    });
  }

  /** All shapes added to this worksheet. */
  getShapes(): ShapeModel[] {
    return this._shapes.slice();
  }

  /**
   * Resolve a shape's `range` into concrete two-cell anchor coordinates,
   * reusing the `Image` range parser so cell-address/anchor handling stays in
   * one place. Returns a serializable ShapeModel for the worksheet xform.
   */
  private _resolveShapeModel(shape: ShapeModel): ShapeModel {
    let range: Extract<ImageModel, { type: "image" }>["range"] | undefined;
    try {
      const probe = new Image(this, { type: "image", imageId: "", range: shape.range });
      // The probe is always an "image" type, so its model carries `range`.
      range = (probe.model as Extract<ImageModel, { type: "image" }>).range;
    } catch {
      // Range could not be parsed into an anchor (addShape validates the common
      // cases up front; this guards exotic inputs). Drop the anchor so the
      // serializer skips this shape rather than failing the whole worksheet.
      range = undefined;
    }
    if (!range) {
      return { ...shape, anchorRange: undefined };
    }
    return {
      ...shape,
      anchorRange: {
        tl: range.tl,
        br: range.br,
        ext: range.ext,
        pos: range.pos,
        editAs: range.editAs
      }
    };
  }

  getImages(): Image[] {
    return this._media.filter(m => m.type === "image");
  }

  // ===========================================================================
  // Charts
  // ===========================================================================

  /**
   * Add a chart to the worksheet, positioned at the given range.
   * Returns the chart number (1-based) that identifies this chart in the workbook.
   */
  addChart(options: AddChartOptions, range: AddChartRange): number {
    const chart = getChartSupport();
    return this._registerChart(chart.buildChartModel(options), range, options);
  }

  addColumnChart(
    options: Omit<AddBarChartOptions, "type" | "barDir">,
    range: AddChartRange
  ): number {
    return this.addChart({ ...options, type: "bar", barDir: "col" }, range);
  }

  addBarChart(options: Omit<AddBarChartOptions, "type" | "barDir">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "bar", barDir: "bar" }, range);
  }

  addLineChart(options: Omit<AddChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "line" }, range);
  }

  addAreaChart(options: Omit<AddChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "area" }, range);
  }

  addPieChart(options: Omit<AddPieChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "pie" }, range);
  }

  addDoughnutChart(options: Omit<AddPieChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "doughnut" }, range);
  }

  addScatterChart(options: Omit<AddScatterChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "scatter" }, range);
  }

  addBubbleChart(options: Omit<AddChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "bubble" }, range);
  }

  addRadarChart(options: Omit<AddChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "radar" }, range);
  }

  addStockChart(options: Omit<AddChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "stock" }, range);
  }

  addSurfaceChart(options: Omit<AddSurfaceChartOptions, "type">, range: AddChartRange): number {
    return this.addChart({ ...options, type: "surface" }, range);
  }

  addHistogramChart(options: Omit<AddChartExOptions, "type">, range: AddChartRange): number {
    return this.addChartEx({ ...options, type: "histogram" }, range);
  }

  addParetoChart(options: Omit<AddChartExOptions, "type">, range: AddChartRange): number {
    return this.addChartEx({ ...options, type: "pareto" }, range);
  }

  addWaterfallChart(options: Omit<AddChartExOptions, "type">, range: AddChartRange): number {
    return this.addChartEx({ ...options, type: "waterfall" }, range);
  }

  addFunnelChart(options: Omit<AddChartExOptions, "type">, range: AddChartRange): number {
    return this.addChartEx({ ...options, type: "funnel" }, range);
  }

  addTreemapChart(options: Omit<AddChartExOptions, "type">, range: AddChartRange): number {
    return this.addChartEx({ ...options, type: "treemap" }, range);
  }

  addSunburstChart(options: Omit<AddChartExOptions, "type">, range: AddChartRange): number {
    return this.addChartEx({ ...options, type: "sunburst" }, range);
  }

  addBoxWhiskerChart(options: Omit<AddChartExOptions, "type">, range: AddChartRange): number {
    return this.addChartEx({ ...options, type: "boxWhisker" }, range);
  }

  addRegionMapChart(options: Omit<AddChartExOptions, "type">, range: AddChartRange): number {
    return this.addChartEx({ ...options, type: "regionMap" }, range);
  }

  addPresetChart(
    preset: ExcelChartPreset,
    options: Omit<AddChartOptions, "type"> & Partial<Pick<AddChartOptions, "type">>,
    range: AddChartRange
  ): number {
    return this.addChart(getChartSupport().applyChartPreset(preset, options), range);
  }

  addPresetChartEx(
    preset: ExcelChartExPreset,
    options: Omit<AddChartExOptions, "type"> & Partial<Pick<AddChartExOptions, "type">>,
    range: AddChartRange
  ): number {
    return this.addChartEx(getChartSupport().applyChartExPreset(preset, options), range);
  }

  seriesFromColumns(
    options: SeriesFromColumnsOptions
  ): ReturnType<ChartSupport["seriesFromColumns"]> {
    return getChartSupport().seriesFromColumns(this.name, options);
  }

  addChartFromTable(
    table: Table | string,
    options: AddChartFromTableOptions,
    range: AddChartRange
  ): number {
    return this.addChart(getChartSupport().chartOptionsFromTable(this, table, options), range);
  }

  addChartFromRows<T extends Record<string, unknown>>(
    rows: T[],
    options: AddChartFromRowsOptions<T>,
    range: AddChartRange
  ): number {
    return this.addChart(getChartSupport().chartOptionsFromRows(this, rows, options), range);
  }

  addColumnChartFromRows<T extends Record<string, unknown>>(
    rows: T[],
    options: Omit<AddChartFromRowsOptions<T>, "type" | "barDir">,
    range: AddChartRange
  ): number {
    return this.addChart(
      getChartSupport().chartOptionsFromRows(this, rows, {
        ...options,
        type: "bar",
        barDir: "col"
      } as AddChartFromRowsOptions<T>),
      range
    );
  }

  /**
   * Add a chartEx chart whose data references come from a worksheet Table.
   * Mirrors {@link addChartFromTable} for the modern `cx:` chart types
   * (sunburst, treemap, waterfall, funnel, histogram, pareto, boxWhisker).
   * `regionMap` is not supported through this helper because its data
   * model expects geographic labels — use {@link addChartEx} directly.
   */
  addChartExFromTable(
    table: Table | string,
    options: AddChartExFromTableOptions & {
      type: Exclude<AddChartExOptions["type"], "regionMap">;
    },
    range: AddChartRange
  ): number {
    return this.addChartEx(getChartSupport().chartExOptionsFromTable(this, table, options), range);
  }

  /**
   * Add a chartEx chart whose rows come from a plain object-array, staged
   * into the worksheet before being referenced by absolute range. Mirrors
   * {@link addChartFromRows} for the modern `cx:` chart types.
   */
  addChartExFromRows<T extends Record<string, unknown>>(
    rows: T[],
    options: AddChartExFromRowsOptions<T> & {
      type: Exclude<AddChartExOptions["type"], "regionMap">;
    },
    range: AddChartRange
  ): number {
    return this.addChartEx(getChartSupport().chartExOptionsFromRows(this, rows, options), range);
  }

  /**
   * Add a classic pivot chart linked to an existing pivot table.
   * Returns the chart number (1-based) that identifies this chart in the workbook.
   */
  addPivotChart(pivotTable: PivotTable, options: AddChartOptions, range: AddChartRange): number {
    const pivotChartOptions = withPivotChartSource(pivotTable, options);
    return this._registerChart(
      getChartSupport().buildChartModel(pivotChartOptions),
      range,
      pivotChartOptions
    );
  }

  /**
   * Add a combo pivot chart linked to an existing pivot table.
   * Returns the chart number (1-based) that identifies this chart in the workbook.
   */
  addPivotComboChart(
    pivotTable: PivotTable,
    options: AddComboChartOptions,
    range: AddChartRange
  ): number {
    const pivotChartOptions = withPivotChartSource(pivotTable, options);
    return this._registerChart(
      getChartSupport().buildComboChartModel(pivotChartOptions),
      range,
      pivotChartOptions
    );
  }

  /**
   * Add a combo chart (multiple chart type groups) to the worksheet.
   * Returns the chart number (1-based) that identifies this chart in the workbook.
   */
  addComboChart(options: AddComboChartOptions, range: AddChartRange): number {
    return this._registerChart(getChartSupport().buildComboChartModel(options), range, options);
  }

  /**
   * Add a ChartEx (Office 2016+ extended chart) to the worksheet.
   *
   * Supported types: `sunburst`, `treemap`, `waterfall`, `funnel`, `histogram`,
   * `pareto`, `boxWhisker`, `regionMap`.
   *
   * Returns the chartEx number (1-based) that identifies this chart in the workbook.
   */
  addChartEx(options: AddChartExOptions, range: AddChartRange): number {
    const model = getChartSupport().buildChartExModel(options);
    return this._registerChartEx(model, range);
  }

  /** @internal Register a built chartEx model into the workbook and this worksheet. */
  private _registerChartEx(model: ChartExModel, range: AddChartRange): number {
    const chart = getChartSupport();
    const chartExNumber = this._workbook.nextChartExNumber();
    try {
      // Pass `this` as context worksheet so sheet-scoped defined names on
      // this sheet take precedence over workbook-scoped names of the same
      // bare name (matches Excel resolution order).
      chart.fillChartExCaches(model, this._workbook, this);
    } catch {
      // Cache population is best-effort; never let it break chart creation.
    }
    this._workbook.addChartExStructuredEntry({ chartExNumber, model });
    const chartInstance = chart.createChart(this, { chartExNumber }, range);
    this._charts.push(chartInstance);
    return chartExNumber;
  }

  /** @internal Register a built chart model into the workbook and this worksheet. */
  private _registerChart(
    chartModel: any,
    range: AddChartRange,
    options?: Pick<AddChartOptions, "chartStyle" | "chartColors">
  ): number {
    const chart = getChartSupport();
    const chartNumber = this._workbook.nextChartNumber();
    // Auto-populate caches so headless consumers see non-empty charts.
    // Excel itself will recompute on open; this is a best-effort enrichment.
    // Pass `this` as context worksheet so sheet-scoped defined names on
    // this sheet take precedence over workbook-scoped names of the same
    // bare name (matches Excel resolution order).
    try {
      chart.fillChartCaches(chartModel, this._workbook, this);
    } catch {
      // Cache population is best-effort; never let it break chart creation.
    }
    const entry = { chartNumber, model: chartModel };
    // Resolve any staged `pictureFill.image` payloads into real workbook
    // media entries + chart relationships. Safe to call before
    // `addChartEntry` so the entry we store already carries the final
    // `entry.rels` array.
    try {
      chart.resolvePendingChartImages(entry, this._workbook, chartNumber);
    } catch {
      // Image resolution is best-effort; a broken image payload should
      // never take down chart creation — the series keeps its
      // `pictureOptions`, just without the blipFill.
    }
    this._workbook.addChartEntry(entry);
    this._applyChartSidecars(chartNumber, options);
    const chartInstance = chart.createChart(this, { chartNumber }, range);
    this._charts.push(chartInstance);
    return chartNumber;
  }

  private _applyChartSidecars(
    chartNumber: number,
    options?: Pick<AddChartOptions, "chartStyle" | "chartColors">
  ): void {
    if (!options?.chartStyle && !options?.chartColors) {
      return;
    }
    const chart = getChartSupport();
    if (options?.chartStyle) {
      this._workbook.setChartStyle(
        chartNumber,
        new TextEncoder().encode(chart.buildChartStyle(options.chartStyle))
      );
    }
    if (options?.chartColors) {
      this._workbook.setChartColors(
        chartNumber,
        new TextEncoder().encode(chart.buildChartColors(options.chartColors))
      );
    }
  }

  /**
   * Get all charts embedded in this worksheet.
   */
  getCharts(): Chart[] {
    return [...this._charts];
  }

  /**
   * Remove a chart from this worksheet.
   *
   * @param chart - Either the chart object or its 0-based index in the worksheet.
   * @returns `true` if a chart was removed, `false` otherwise.
   */
  removeChart(chart: Chart | number): boolean {
    const idx = typeof chart === "number" ? chart : this._charts.indexOf(chart);
    if (idx < 0 || idx >= this._charts.length) {
      return false;
    }
    const removed = this._charts.splice(idx, 1)[0];
    if (removed.chartNumber > 0) {
      this._workbook.removeChartEntry?.(removed.chartNumber);
    }
    if (removed.chartExNumber > 0) {
      this._workbook.removeChartExStructuredEntry?.(removed.chartExNumber);
    }
    // Prune the matching anchor from the loaded drawing so the writer
    // doesn't emit a dangling rel pointing at the now-removed chart
    // part. The drawing xform regenerates rels from `drawing.anchors`
    // on every write (see `worksheet-xform.ts` chart-anchor reconcile),
    // so dropping the anchor here is sufficient — we don't need to
    // hand-edit `drawing.rels` ourselves.
    const drawing = this._drawing as
      | { anchors?: Array<{ chartNumber?: number; chartExNumber?: number }> }
      | undefined;
    if (drawing?.anchors) {
      drawing.anchors = drawing.anchors.filter(anchor => {
        if (removed.chartNumber > 0 && anchor.chartNumber === removed.chartNumber) {
          return false;
        }
        if (removed.chartExNumber > 0 && anchor.chartExNumber === removed.chartExNumber) {
          return false;
        }
        return true;
      });
    }
    return true;
  }

  // ===========================================================================
  // Sparklines (x14:sparklineGroups)
  // ===========================================================================

  /**
   * Add a sparkline group to this worksheet.
   *
   * A sparkline group is a collection of small in-cell charts sharing common
   * styling. Sparklines are rendered via `x14:sparklineGroups` inside the
   * worksheet extension list.
   *
   * @returns The newly added SparklineGroup for further modification.
   */
  addSparklineGroup(options: AddSparklineGroupOptions): SparklineGroup {
    const group = buildSparklineGroup(options);
    this._sparklineGroups.push(group);
    return group;
  }

  /** Get all sparkline groups on this worksheet. */
  getSparklineGroups(): SparklineGroup[] {
    return [...this._sparklineGroups];
  }

  /** @internal Used by the worksheet xform to access raw storage. */
  get sparklineGroups(): SparklineGroup[] {
    return this._sparklineGroups;
  }

  /** @internal Used when rebuilding from a parsed model. */
  set sparklineGroups(value: SparklineGroup[]) {
    this._sparklineGroups = value ?? [];
  }

  /**
   * Remove a sparkline group.
   *
   * @param groupOrIndex - The group object or its 0-based index.
   * @returns `true` if removed, `false` otherwise.
   */
  removeSparklineGroup(groupOrIndex: SparklineGroup | number): boolean {
    const idx =
      typeof groupOrIndex === "number" ? groupOrIndex : this._sparklineGroups.indexOf(groupOrIndex);
    if (idx < 0 || idx >= this._sparklineGroups.length) {
      return false;
    }
    this._sparklineGroups.splice(idx, 1);
    return true;
  }

  /**
   * Using the image id from `Workbook.addImage`, set the background to the worksheet.
   *
   * The image must be **embedded** (`buffer`/`base64`/`filename`). Worksheet
   * background pictures (`<picture r:id>`) do not support external (linked)
   * images — Excel silently drops a background whose relationship uses
   * `TargetMode="External"`, so this rejects linked images up front.
   */
  addBackgroundImage(imageId: string | number): void {
    const bookImage = this._workbook.getImage(imageId);
    if (bookImage && isExternalImage(bookImage)) {
      throw new ImageError(
        "Background images cannot be external (linked) images. " +
          "Use an embedded image (buffer/base64/filename). " +
          "External images are only supported for cell pictures and overlay watermarks."
      );
    }
    const model = {
      type: "background",
      imageId: String(imageId)
    };
    this._media.push(new Image(this, model));
  }

  getBackgroundImageId(): string | undefined {
    const image = this._media.find(m => m.type === "background");
    return image && image.imageId;
  }

  // =========================================================================
  // Watermark

  /**
   * Add a watermark to the worksheet using an image from `workbook.addImage()`.
   *
   * The watermark can be placed in one of two modes:
   *
   * - **overlay** (default): Places the watermark image as a drawing on top of cells.
   *   Visible on screen AND when printed. Supports transparency via DrawingML `alphaModFix`.
   *
   * - **header**: Places the watermark image in the page header using VML.
   *   Visible in Page Layout view and when printed. Renders behind cell content.
   *   Transparency must be baked into the image (PNG with alpha channel).
   *
   * **External (linked) images:** `mode: "overlay"` supports external images
   * (registered via `workbook.addImage({ link })`). `mode: "header"` does
   * **not** — VML header/footer images require embedded media, so passing a
   * linked image with `mode: "header"` throws an `ImageError`. Use an embedded
   * image (`buffer`/`base64`/`filename`) or switch to `mode: "overlay"`.
   *
   * @param options - Watermark configuration
   * @throws {ImageError} If `mode: "header"` is used with an external (linked) image.
   *
   * @example Overlay watermark with transparency:
   * ```typescript
   * const imgId = workbook.addImage({ buffer: pngData, extension: "png" });
   * worksheet.addWatermark({ imageId: imgId, opacity: 0.15 });
   * ```
   *
   * @example Header watermark (behind content):
   * ```typescript
   * const imgId = workbook.addImage({ buffer: pngData, extension: "png" });
   * worksheet.addWatermark({ imageId: imgId, mode: "header" });
   * ```
   */
  addWatermark(options: WatermarkOptions): void {
    const mode = options.mode ?? "overlay";

    // Validate BEFORE mutating any state: VML header/footer images use
    // embedded media (`<v:imagedata o:relid>`); external (linked) images are
    // not representable here. Reject them up front so a failed call leaves the
    // existing watermark untouched (no partial mutation).
    if (mode === "header") {
      const bookImage = this._workbook.getImage(options.imageId);
      if (bookImage && isExternalImage(bookImage)) {
        throw new ImageError(
          "Header watermark images cannot be external (linked) images. " +
            "Use an embedded image (buffer/base64/filename), or use overlay mode for linked images."
        );
      }
    }

    // Remove any existing watermark media entries first
    this._media = this._media.filter(m => m.type !== "watermark" && m.type !== "headerImage");

    this._watermark = {
      imageId: String(options.imageId),
      mode,
      opacity: options.opacity,
      headerWidth: options.headerWidth,
      headerHeight: options.headerHeight,
      applyTo: options.applyTo
    };

    if (this._watermark.mode === "overlay") {
      // Add as a special "watermark" media entry for the drawing pipeline
      const model = {
        type: "watermark",
        imageId: String(options.imageId),
        opacity: options.opacity
      };
      this._media.push(new Image(this, model as any));
    } else {
      // Header mode: add as a "headerImage" media entry for the VML pipeline.
      const model = {
        type: "headerImage",
        imageId: String(options.imageId),
        headerWidth: options.headerWidth,
        headerHeight: options.headerHeight,
        applyTo: options.applyTo
      };
      this._media.push(new Image(this, model as any));
    }
  }

  /**
   * Get the current watermark configuration, or null if none is set.
   */
  getWatermark(): WatermarkOptions | null {
    return this._watermark;
  }

  /**
   * Remove the watermark from the worksheet.
   */
  removeWatermark(): void {
    this._watermark = null;
    this._media = this._media.filter(m => m.type !== "watermark" && m.type !== "headerImage");
  }

  // =========================================================================
  // Form Controls (Legacy Checkboxes)

  /**
   * Add a form control checkbox to the worksheet.
   *
   * Form control checkboxes are the legacy style that work in Office 2007+,
   * WPS Office, LibreOffice, and other spreadsheet applications.
   *
   * Unlike modern in-cell checkboxes (which only work in Microsoft 365),
   * form control checkboxes are floating controls positioned over cells.
   *
   * @param range - Cell reference (e.g., "B2") or range (e.g., "B2:D3") for positioning
   * @param options - Checkbox options
   * @returns The created FormCheckbox instance
   *
   * @example
   * // Simple checkbox at B2
   * ws.addFormCheckbox("B2");
   *
   * // Checkbox with label and linked cell
   * ws.addFormCheckbox("B2:D3", {
   *   text: "Accept terms",
   *   link: "A2",
   *   checked: false
   * });
   */
  addFormCheckbox(range: FormControlRange, options?: FormCheckboxOptions): FormCheckbox {
    const checkbox = new FormCheckbox(this, range, options);
    this.formControls.push(checkbox);
    return checkbox;
  }

  /**
   * Get all form control checkboxes in the worksheet
   */
  getFormCheckboxes(): FormCheckbox[] {
    return this.formControls;
  }

  // =========================================================================
  // Worksheet Protection

  /**
   * Protect the worksheet with optional password and options
   */
  async protect(password?: string, options?: Partial<SheetProtection>): Promise<void> {
    this.sheetProtection = await buildSheetProtection(password, options);
  }

  unprotect(): void {
    this.sheetProtection = null;
  }

  // =========================================================================
  // Tables

  /**
   * Add a new table and return a reference to it
   */
  addTable(model: TableProperties): Table {
    const table = new Table(this, model);
    // table.name is sanitized by Table.validate() — check against the
    // sanitized name so that e.g. "My Table" and "My_Table" (which both
    // sanitize to "My_Table") are correctly detected as duplicates.
    const nameKey = table.name.toLowerCase();
    if (this.tables[table.name]) {
      throw new TableError(
        `Table name "${table.name}" already exists in worksheet "${this.name}".`
      );
    }
    if (this.workbook._tableNames.has(nameKey)) {
      throw new TableError(
        `Table name "${table.name}" already exists in another worksheet. ` +
          `Table names must be unique across the entire workbook (case-insensitive).`
      );
    }
    this.tables[table.name] = table;
    this.workbook._tableNames.add(nameKey);
    return table;
  }

  /**
   * Fetch table by name
   */
  getTable(name: string): Table {
    return this.tables[name];
  }

  /**
   * Delete table by name
   */
  removeTable(name: string): void {
    if (this.tables[name]) {
      this.workbook._tableNames.delete(name.toLowerCase());
    }
    delete this.tables[name];
  }

  /**
   * Fetch all tables in the worksheet
   */
  getTables(): Table[] {
    return Object.values(this.tables);
  }

  // =========================================================================
  // Pivot Tables
  addPivotTable(model: PivotTableModel): PivotTable {
    const pivotTable = makePivotTable(this, model);

    this.pivotTables.push(pivotTable);
    this.workbook.pivotTables.push(pivotTable);

    return pivotTable;
  }

  // ===========================================================================
  // Conditional Formatting

  /**
   * Add conditional formatting rules
   */
  addConditionalFormatting(cf: ConditionalFormattingOptions): void {
    this.conditionalFormattings.push(cf);
  }

  /**
   * Delete conditional formatting rules.
   *
   * - When `filter` is a number, removes the rule at that index.
   * - When `filter` is a function, removes every rule for which it returns `true`
   *   (i.e. the predicate selects the rules to delete, not the ones to keep).
   * - When `filter` is omitted/falsy, removes all rules.
   */
  removeConditionalFormatting(
    filter?:
      | number
      | ((
          value: ConditionalFormattingOptions,
          index: number,
          array: ConditionalFormattingOptions[]
        ) => boolean)
  ): void {
    if (typeof filter === "number") {
      this.conditionalFormattings.splice(filter, 1);
    } else if (typeof filter === "function") {
      // Keep entries for which the predicate returns false; drop the matches.
      this.conditionalFormattings = this.conditionalFormattings.filter(
        (value, index, array) => !filter(value, index, array)
      );
    } else {
      this.conditionalFormattings = [];
    }
  }

  // ===========================================================================
  // Auto-Fit Column Width & Row Height

  /**
   * Auto-fit a single column's width to its content.
   *
   * Calculates the optimal column width by measuring the display text of every
   * cell in the column, considering font, number format, bold/italic, and CJK
   * characters. Sets `column.width` and `column.bestFit = true`.
   *
   * @param col - Column number (1-based) or column letter (e.g. "A")
   * @returns The worksheet (for chaining)
   */
  autoFitColumn(col: number | string): this {
    const colNum = typeof col === "string" ? colCache.l2n(col) : col;
    this._autoFitColumnImpl(colNum);
    return this;
  }

  /**
   * Auto-fit all columns (or a range of columns) to their content.
   *
   * @param startCol - Start column (1-based number or letter). Defaults to first column.
   * @param endCol - End column (1-based number or letter). Defaults to last column.
   * @returns The worksheet (for chaining)
   */
  autoFitColumns(startCol?: number | string, endCol?: number | string): this {
    const dims = this.dimensions;
    if (!dims || dims.left === undefined) {
      return this;
    }
    const start =
      startCol != null
        ? typeof startCol === "string"
          ? colCache.l2n(startCol)
          : startCol
        : dims.left;
    const end =
      endCol != null ? (typeof endCol === "string" ? colCache.l2n(endCol) : endCol) : dims.right;

    for (let c = start; c <= end; c++) {
      this._autoFitColumnImpl(c);
    }
    return this;
  }

  /**
   * Auto-fit a single row's height to its content.
   *
   * @param rowNumber - Row number (1-based)
   * @returns The worksheet (for chaining)
   */
  autoFitRow(rowNumber: number): this {
    this._autoFitRowImpl(rowNumber);
    return this;
  }

  /**
   * Auto-fit all rows (or a range of rows) to their content.
   *
   * @param startRow - Start row (1-based). Defaults to first row.
   * @param endRow - End row (1-based). Defaults to last row.
   * @returns The worksheet (for chaining)
   */
  autoFitRows(startRow?: number, endRow?: number): this {
    const dims = this.dimensions;
    if (!dims || dims.top === undefined) {
      return this;
    }
    const start = startRow ?? dims.top;
    const end = endRow ?? dims.bottom;

    for (let r = start; r <= end; r++) {
      this._autoFitRowImpl(r);
    }
    return this;
  }

  /** @internal Implementation of column auto-fit */
  private _autoFitColumnImpl(colNum: number): void {
    const mdw = getMaxDigitWidth(); // default font MDW

    // Check if this column is under an autofilter
    const hasAutoFilter = this._isColumnInAutoFilter(colNum);

    let maxWidthPx = 0;

    // Iterate all rows
    this._rows.forEach(row => {
      if (!row) {
        return;
      }
      // Skip hidden rows — Excel excludes them from auto-fit
      if (row.hidden) {
        return;
      }
      const cell = row.findCell(colNum);
      if (!cell) {
        return;
      }

      // Skip merged cell slaves — the content belongs to the master cell.
      // For the master cell of a multi-column merge, skip too (the width
      // should not be attributed to a single column).
      if (cell.type === Enums.ValueType.Merge) {
        return;
      }
      if (cell.isMerged) {
        // This is a master cell with merges spanning multiple columns
        const mergeRange = this._merges[cell.address];
        if (mergeRange && mergeRange.left !== mergeRange.right) {
          return; // multi-column merge — skip
        }
      }

      // Skip shrinkToFit cells — they adapt to the column, not vice versa
      if (cell.alignment?.shrinkToFit) {
        return;
      }

      let textWidthPx = getCellTextWidthPx(cell);

      // Account for indent: each level adds approximately 3 character widths
      const indent = cell.alignment?.indent;
      if (indent && indent > 0) {
        textWidthPx += indent * 3 * mdw;
      }

      if (textWidthPx > maxWidthPx) {
        maxWidthPx = textWidthPx;
      }
    });

    if (maxWidthPx > 0) {
      const charWidth = calculateAutoFitWidth(maxWidthPx, mdw, hasAutoFilter);
      if (charWidth > 0) {
        const column = this.getColumn(colNum);
        column.width = charWidth;
        column.bestFit = true;
      }
    }
  }

  /** @internal Implementation of row auto-fit */
  private _autoFitRowImpl(rowNumber: number): void {
    const row = this._rows[rowNumber - 1];
    if (!row) {
      return;
    }

    const mdw = getMaxDigitWidth();
    let maxHeightPt = 0;

    row.eachCell(cell => {
      // Skip merged cell slaves
      if (cell.type === Enums.ValueType.Merge) {
        return;
      }
      // Skip multi-row merged masters
      if (cell.isMerged) {
        const mergeRange = this._merges[cell.address];
        if (mergeRange && mergeRange.top !== mergeRange.bottom) {
          return;
        }
      }
      // Skip cells in hidden columns
      const col = this._columns[cell.col - 1];
      if (col?.hidden) {
        return;
      }

      const columnWidthPx = this._getColumnContentWidthForCell(cell, mdw);
      const heightPt = getCellHeightPt(cell, mdw, columnWidthPx);
      if (heightPt > maxHeightPt) {
        maxHeightPt = heightPt;
      }
    });

    if (maxHeightPt > 0) {
      row.height = Math.ceil(maxHeightPt * 4) / 4; // Round to nearest 0.25pt (Excel precision)
      row.customHeight = true;
    }
  }

  /**
   * @internal Get the content width of the column a cell belongs to, in pixels.
   * Uses the explicit column width if set, otherwise falls back to the worksheet
   * default or the Excel default (9 character units ≈ 64px).
   */
  private _getColumnContentWidthForCell(cell: Cell, mdw: number): number | undefined {
    if (!cell.alignment?.wrapText) {
      return undefined;
    }
    // Try to get explicit column width; avoid creating a column as side effect
    const col = this._columns[cell.col - 1];
    const colWidth = col?.width ?? this.properties.defaultColWidth ?? 9;
    return getColumnContentWidthPx(colWidth, mdw);
  }

  /** @internal Check if a column falls within the autoFilter range */
  private _isColumnInAutoFilter(colNum: number): boolean {
    if (!this.autoFilter) {
      return false;
    }
    if (typeof this.autoFilter === "string") {
      const range = colCache.decode(this.autoFilter) as DecodedRange;
      return colNum >= range.left && colNum <= range.right;
    }
    const { from, to } = this.autoFilter;
    const fromCol =
      typeof from === "string" ? (colCache.decode(from) as { col: number }).col : from.col;
    const toCol = typeof to === "string" ? (colCache.decode(to) as { col: number }).col : to.col;
    return colNum >= fromCol && colNum <= toCol;
  }

  // ===========================================================================
  // Model

  get model(): WorksheetModel {
    const model: WorksheetModel = {
      id: this.id,
      name: this.name,
      dataValidations: this.dataValidations.model,
      properties: this.properties,
      state: this.state,
      pageSetup: this.pageSetup,
      headerFooter: this.headerFooter,
      rowBreaks: this.rowBreaks,
      colBreaks: this.colBreaks,
      views: this.views,
      autoFilter: this.autoFilter,
      media: this._media.map(medium => medium.model),
      shapes: this._shapes.map(shape => this._resolveShapeModel(shape)),
      sheetProtection: this.sheetProtection,
      tables: Object.values(this.tables).map(table => table.model),
      pivotTables: this.pivotTables,
      conditionalFormattings: this.conditionalFormattings,
      formControls: this.formControls.map(fc => fc.model),
      ignoredErrors: this.ignoredErrors,
      watermark: this._watermark,
      drawing: this._drawing,
      charts: this._charts.map(c => c.model),
      sparklineGroups: this._sparklineGroups,
      threadedComments: this.threadedComments
    };

    // =================================================
    // columns
    model.cols = Column.toModel(this.columns);

    // ==========================================================
    // Rows
    const rows: RowModel[] = (model.rows = []);
    const dimensions: Range = (model.dimensions = new Range());
    this._rows.forEach(row => {
      const rowModel = row && row.model;
      if (rowModel) {
        dimensions.expand(rowModel.number, rowModel.min, rowModel.number, rowModel.max);
        rows.push(rowModel);
      }
    });

    // ==========================================================
    // Merges
    model.mergeCells = Object.values(this._merges).map((merge: Range) => merge.range);

    return model;
  }

  private _parseRows(model: WorksheetModel): void {
    this._rows = [];
    if (model.rows) {
      model.rows.forEach(rowModel => {
        const row = new Row(this, rowModel.number);
        this._rows[row.number - 1] = row;
        row.model = rowModel;
      });
    }
  }

  private _parseMergeCells(model: WorksheetModel): void {
    if (model.mergeCells) {
      model.mergeCells.forEach((merge: string) => {
        // Do not merge styles when importing an Excel file
        // since each cell may have different styles intentionally.
        this.mergeCellsWithoutStyle(merge);
      });
    }
  }

  set model(value: WorksheetModel) {
    this.name = value.name;
    this.state = value.state;
    this._columns = Column.fromModel(this, value.cols ?? []);
    this._parseRows(value);

    this._parseMergeCells(value);
    this.dataValidations = new DataValidations(value.dataValidations);
    this.properties = value.properties;
    this.pageSetup = value.pageSetup;
    this.headerFooter = value.headerFooter;
    this.rowBreaks = value.rowBreaks ?? [];
    this.colBreaks = value.colBreaks ?? [];
    this.views = value.views;
    this.autoFilter = value.autoFilter;
    this._media = value.media.map(medium => new Image(this, medium));
    this._shapes = value.shapes ? value.shapes.slice() : [];
    // Restore watermark state from media entries
    this._watermark = value.watermark ?? null;
    if (!this._watermark) {
      for (const medium of this._media) {
        if (medium.type === "watermark") {
          this._watermark = {
            imageId: medium.imageId ?? "",
            mode: "overlay",
            opacity: medium.opacity
          };
          break;
        } else if (medium.type === "headerImage") {
          this._watermark = {
            imageId: medium.imageId ?? "",
            mode: "header",
            headerWidth: medium.headerWidth,
            headerHeight: medium.headerHeight
          };
          break;
        }
      }
    }
    this.sheetProtection = value.sheetProtection;
    this.tables = value.tables.reduce((tables: { [key: string]: Table }, table: TableModel) => {
      const t = new Table(this, table);
      t.model = table;
      tables[table.name] = t;
      this.workbook._tableNames.add(table.name.toLowerCase());
      return tables;
    }, {});
    this.pivotTables = value.pivotTables;
    for (const pivotTable of this.pivotTables ?? []) {
      pivotTable.worksheetName ??= this.name;
      pivotTable.name ??= `PivotTable${pivotTable.tableNumber}`;
    }
    this.conditionalFormattings = value.conditionalFormattings;
    this.ignoredErrors = value.ignoredErrors ?? [];
    this.threadedComments = value.threadedComments ?? [];
    // Rebuild form controls from the serialised model so importSheet() and any
    // other model round-trip preserves checkbox state, position, and links.
    this.formControls = (value.formControls ?? []).map(fcModel =>
      FormCheckbox.fromModel(this, fcModel)
    );
    // Preserve loaded drawing data (charts, etc.)
    this._drawing = value.drawing;
    // Restore chart objects from model (explicit charts array) or from drawing anchors.
    // If chart support is not installed we still preserve the chart data inside
    // `workbook.charts` entries (the serialiser reads from there), but we leave
    // `_charts` empty because the `Chart` wrapper class lives in the optional
    // chart module. Consumers who want to *manipulate* charts must install
    // chart support; consumers doing pure load-save pass-through don't need to.
    const chartHost = tryGetChartSupport();
    if (chartHost && value.charts && value.charts.length > 0) {
      this._charts = value.charts.map((c: ChartAnchorModel) =>
        chartHost.createChart(
          this,
          { chartNumber: c.chartNumber, chartExNumber: c.chartExNumber },
          c.range
        )
      );
    } else if (chartHost && (value.drawing as any)?.anchors) {
      // Extract chart anchors from drawing (loaded from XLSX)
      this._charts = ((value.drawing as any).anchors as any[])
        .filter((a: any) => a.chartNumber || a.chartExNumber)
        .map((a: any) =>
          chartHost.createChart(
            this,
            { chartNumber: a.chartNumber ?? 0, chartExNumber: a.chartExNumber ?? 0 },
            a.range
          )
        );
    } else {
      this._charts = [];
    }
  }

  // ===========================================================================
  // Data Conversion — JSON
  // ===========================================================================

  /**
   * Convert worksheet data to a JSON array.
   *
   * @example
   * // Default: first row as headers, returns array of objects
   * const data = ws.toJSON();
   * // => [{name: "Alice", age: 30}, {name: "Bob", age: 25}]
   *
   * @example
   * // Array of arrays
   * const aoa = ws.toJSON({ header: 1 });
   * // => [["name", "age"], ["Alice", 30], ["Bob", 25]]
   *
   * @example
   * // Column letters as keys
   * const cols = ws.toJSON({ header: "A" });
   * // => [{A: "name", B: "age"}, {A: "Alice", B: 30}]
   */
  toJSON(opts: SheetToJSONOptions & { header: 1 }): CellValue[][];
  toJSON(opts?: SheetToJSONOptions): Record<string, CellValue>[];
  toJSON(opts?: SheetToJSONOptions): CellValue[][] | Record<string, CellValue>[] {
    const o = opts || {};

    // Determine range
    let startRow = 1;
    let endRow = this.rowCount;
    let startCol = 1;
    let endCol = this.columnCount;

    if (o.range !== undefined) {
      if (typeof o.range === "number") {
        startRow = o.range + 1; // 0-indexed to 1-indexed
      } else if (typeof o.range === "string") {
        const r = decodeRange(o.range);
        startRow = r.s.r + 1;
        endRow = r.e.r + 1;
        startCol = r.s.c + 1;
        endCol = r.e.c + 1;
      }
    }

    if (endRow < startRow || endCol < startCol) {
      return [];
    }

    const headerOpt = o.header;

    // header: 1 — return array of arrays
    if (headerOpt === 1) {
      const result: CellValue[][] = [];
      const includeBlank = o.blankRows !== false;

      for (let row = startRow; row <= endRow; row++) {
        const rowData: CellValue[] = [];
        let isEmpty = true;

        for (let col = startCol; col <= endCol; col++) {
          const cell = this.getCell(row, col);
          const val = o.raw === false ? getCellDisplayText(cell, o.dateFormat).trim() : cell.value;

          if (val != null && val !== "") {
            rowData[col - startCol] = val;
            isEmpty = false;
          } else if (o.defaultValue !== undefined) {
            rowData[col - startCol] = o.defaultValue;
          } else {
            rowData[col - startCol] = null;
          }
        }

        if (!isEmpty || includeBlank) {
          result.push(rowData);
        }
      }

      return result;
    }

    // header: "A" — use column letters as keys
    if (headerOpt === "A") {
      const result: Record<string, CellValue>[] = [];
      const includeBlank = o.blankRows === true;

      for (let row = startRow; row <= endRow; row++) {
        const rowData: Record<string, CellValue> = {};
        let isEmpty = true;

        for (let col = startCol; col <= endCol; col++) {
          const cell = this.getCell(row, col);
          const val = o.raw === false ? getCellDisplayText(cell, o.dateFormat).trim() : cell.value;
          const key = encodeCol(col - 1);

          if (val != null && val !== "") {
            rowData[key] = val;
            isEmpty = false;
          } else if (o.defaultValue !== undefined) {
            rowData[key] = o.defaultValue;
          }
        }

        if (!isEmpty || includeBlank) {
          result.push(rowData);
        }
      }

      return result;
    }

    // header: string[] — use provided array as keys
    if (Array.isArray(headerOpt)) {
      const result: Record<string, CellValue>[] = [];
      const includeBlank = o.blankRows === true;

      for (let row = startRow; row <= endRow; row++) {
        const rowData: Record<string, CellValue> = {};
        let isEmpty = true;

        for (let col = startCol; col <= endCol; col++) {
          const colIdx = col - startCol;
          const key = headerOpt[colIdx] ?? `__EMPTY_${colIdx}`;
          const cell = this.getCell(row, col);
          const val = o.raw === false ? getCellDisplayText(cell, o.dateFormat).trim() : cell.value;

          if (val != null && val !== "") {
            rowData[key] = val;
            isEmpty = false;
          } else if (o.defaultValue !== undefined) {
            rowData[key] = o.defaultValue;
          }
        }

        if (!isEmpty || includeBlank) {
          result.push(rowData);
        }
      }

      return result;
    }

    // Default: first row as header, disambiguate duplicates
    const headers: string[] = [];
    const headerCounts: Record<string, number> = {};

    for (let col = startCol; col <= endCol; col++) {
      const cell = this.getCell(startRow, col);
      const val = cell.value;
      let header = val != null ? String(val) : `__EMPTY_${col - startCol}`;

      if (headerCounts[header] !== undefined) {
        headerCounts[header]++;
        header = `${header}_${headerCounts[header]}`;
      } else {
        headerCounts[header] = 0;
      }

      headers.push(header);
    }

    const result: Record<string, CellValue>[] = [];
    const dataStartRow = startRow + 1;
    const includeBlank = o.blankRows === true;

    for (let row = dataStartRow; row <= endRow; row++) {
      const rowData: Record<string, CellValue> = {};
      let isEmpty = true;

      for (let col = startCol; col <= endCol; col++) {
        const cell = this.getCell(row, col);
        const val = o.raw === false ? getCellDisplayText(cell, o.dateFormat).trim() : cell.value;
        const key = headers[col - startCol];

        if (val != null && val !== "") {
          rowData[key] = val;
          isEmpty = false;
        } else if (o.defaultValue !== undefined) {
          rowData[key] = o.defaultValue;
        }
      }

      if (!isEmpty || includeBlank) {
        result.push(rowData);
      }
    }

    return result;
  }

  /**
   * Add data from a JSON array to this worksheet.
   * Each object's keys become column headers (written in the first row unless skipHeader is set).
   *
   * @example
   * ws.addJSON([{name: "Alice", age: 30}, {name: "Bob", age: 25}]);
   *
   * @returns this (for chaining)
   */
  addJSON(data: Record<string, CellValue>[], opts?: AddJSONOptions): this {
    if (data.length === 0) {
      return this;
    }

    const o = opts || {};

    // Determine starting position
    let startRow = 1;
    let startCol = 1;
    if (o.origin !== undefined) {
      const resolved = _resolveOrigin(o.origin, this.rowCount);
      startRow = resolved.row;
      startCol = resolved.col;
    }

    // Determine headers
    const allKeys = new Set<string>();
    data.forEach(row => Object.keys(row).forEach(k => allKeys.add(k)));
    const headers = o.header ? [...o.header] : [...allKeys];
    if (o.header) {
      allKeys.forEach(k => {
        if (!headers.includes(k)) {
          headers.push(k);
        }
      });
    }

    let rowNum = startRow;

    // Write header row
    if (!o.skipHeader) {
      headers.forEach((h, colIdx) => {
        this.getCell(rowNum, startCol + colIdx).value = h;
      });
      rowNum++;
    }

    // Write data rows
    for (const row of data) {
      headers.forEach((key, colIdx) => {
        const val = row[key];
        if (val === null && o.nullError) {
          this.getCell(rowNum, startCol + colIdx).value = { error: "#NULL!" };
        } else if (val !== undefined && val !== null) {
          this.getCell(rowNum, startCol + colIdx).value = val;
        }
      });
      rowNum++;
    }

    return this;
  }

  // ===========================================================================
  // Data Conversion — Array of Arrays
  // ===========================================================================

  /**
   * Convert worksheet data to an array of arrays.
   *
   * @example
   * const aoa = ws.toAOA();
   * // => [["Name", "Age"], ["Alice", 30], ["Bob", 25]]
   */
  toAOA(): CellValue[][] {
    const result: CellValue[][] = [];

    this.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      const rowData: CellValue[] = [];
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        rowData[colNumber - 1] = cell.value;
      });
      result[rowNumber - 1] = rowData;
    });

    return result;
  }

  /**
   * Add data from an array of arrays to this worksheet.
   *
   * @example
   * ws.addAOA([["Name", "Age"], ["Alice", 30], ["Bob", 25]]);
   *
   * @returns this (for chaining)
   */
  addAOA(data: CellValue[][], opts?: AddAOAOptions): this {
    if (data.length === 0) {
      return this;
    }

    let startRow = 1;
    let startCol = 1;
    if (opts?.origin !== undefined) {
      const resolved = _resolveOrigin(opts.origin, this.rowCount);
      startRow = resolved.row;
      startCol = resolved.col;
    }

    data.forEach((row, rowIdx) => {
      if (!row) {
        return;
      }
      row.forEach((val, colIdx) => {
        if (val !== undefined && val !== null) {
          this.getCell(startRow + rowIdx, startCol + colIdx).value = val;
        }
      });
    });

    return this;
  }
}

// =============================================================================
// Option Types for Data Conversion
// =============================================================================

export interface SheetToJSONOptions {
  /**
   * Control output format:
   * - `1`: Generate an array of arrays
   * - `"A"`: Row object keys are literal column labels (A, B, C, ...)
   * - `string[]`: Use specified strings as keys in row objects
   * - `undefined`: Read and disambiguate first row as keys
   */
  header?: 1 | "A" | string[];
  /**
   * Override range:
   * - `number`: Use worksheet range but set starting row to the value (0-indexed)
   * - `string`: Use specified range (A1-style bounded range string)
   * - `undefined`: Use worksheet range
   */
  range?: number | string;
  /** Use raw values (true, default) or formatted text strings (false) */
  raw?: boolean;
  /** Default value for empty cells */
  defaultValue?: CellValue;
  /** Include blank rows in output (default: true for AOA, false for objects) */
  blankRows?: boolean;
  /** Override format for date values (only applies when raw: false) */
  dateFormat?: string;
}

export interface AddJSONOptions {
  /** Use specified field order (default: Object.keys from data) */
  header?: string[];
  /** If true, do not include header row in output */
  skipHeader?: boolean;
  /** Starting position: cell address string, {c, r} object, row number (0-indexed), or -1 to append */
  origin?: Origin;
  /** If true, emit #NULL! error cells for null values */
  nullError?: boolean;
}

export interface AddAOAOptions {
  /** Starting position: cell address string, {c, r} object, row number (0-indexed), or -1 to append */
  origin?: Origin;
}

// =============================================================================
// Private Helpers
// =============================================================================

/** Resolve an Origin value to 1-indexed {row, col} for internal use */
function _resolveOrigin(origin: Origin, rowCount: number): { row: number; col: number } {
  if (typeof origin === "string") {
    const addr = decodeCell(origin);
    return { row: addr.r + 1, col: addr.c + 1 };
  }
  if (typeof origin === "number") {
    if (origin === -1) {
      return { row: rowCount + 1, col: 1 };
    }
    return { row: origin + 1, col: 1 };
  }
  return { row: origin.r + 1, col: origin.c + 1 };
}

export { Worksheet, type WorksheetModel };
